import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { capturePartial } from "../hooks/useVisitor";
import { trackLead } from "../lib/metaPixel";

const SEEN_KEY = "pm_exit_intent_seen";

// A one-time exit-intent capture: when the visitor's mouse leaves toward the tab
// bar (or, on mobile, after a back-gesture-ish scroll), offer to take a number so
// interest isn't lost. Submitting sends a soft "partial" capture (an incomplete
// lead the agent can chase). Shown at most once per browser.
export function ExitIntent({ token, listingLabel }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(SEEN_KEY) === "1"; } catch { /* ignore */ }
    if (seen) return;

    const trigger = () => {
      setOpen(true);
      try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
      cleanup();
    };
    const onMouseOut = (e) => {
      // Mouse left through the top of the viewport (toward tabs/close).
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    // Fallback for touch: fire after a short dwell so mobile users see it too.
    const timer = setTimeout(trigger, 35000);

    document.addEventListener("mouseout", onMouseOut);
    function cleanup() {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(timer);
    }
    return cleanup;
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const p = phone.trim();
    if (!/^[6-9]\d{9}$/.test(p)) return;
    capturePartial(token, { name: "", phone: p, email: "" });
    trackLead({ content_name: "Exit intent" });
    setDone(true);
    setTimeout(() => setOpen(false), 1800);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.94, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[420px] rounded-2xl bg-[#FBFAF6] p-7 shadow-[0_20px_60px_rgba(20,19,15,0.35)]"
          >
            <button type="button" onClick={() => setOpen(false)} className="absolute right-3 top-3 text-[#78746A] hover:text-[#14130F]" aria-label="Close">
              <X className="h-5 w-5" />
            </button>

            {done ? (
              <div className="py-4 text-center">
                <div className="text-[1.3rem] font-bold text-[#14130F]">Thank you!</div>
                <p className="mt-2 font-sans text-[0.92rem] text-[#4A473E]">We’ll call you back shortly.</p>
              </div>
            ) : (
              <>
                <div className="text-[0.72rem] font-bold uppercase tracking-[0.16em] text-[#B7975A]">Before you go</div>
                <h3 className="mt-2 text-[1.5rem] font-bold leading-tight text-[#14130F]">
                  Leave your number{listingLabel ? " —" : ""} and we’ll call you back.
                </h3>
                <p className="mt-2 font-sans text-[0.92rem] text-[#4A473E]">
                  {listingLabel ? `Interested in ${listingLabel}? ` : ""}No spam — just a quick call from our team.
                </p>
                <form onSubmit={submit} className="mt-4 flex gap-2 font-sans">
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[6-9][0-9]{9}"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="10-digit mobile number"
                    className="flex-1 rounded-[10px] border border-[#C9C3B4] bg-white px-[0.85rem] py-[0.7rem] text-[1rem] outline-none focus:border-[#2C4A87] focus:ring-2 focus:ring-[#2C4A87]/30"
                  />
                  <button type="submit" className="rounded-[10px] bg-[#2C4A87] px-4 py-2 font-semibold text-white hover:bg-[#1F3766]">
                    Call me
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
